import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, RotateCcw } from 'lucide-react';
import authFetch from '../../utils/authFetch';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const TransactionTable = ({ endpoint, title }) => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // URL Params for Deep Linking
  const [searchParams] = useSearchParams();
  const initialCol = searchParams.get('column') || '';
  const initialSearch = searchParams.get('search') || '';

  const [headers, setHeaders] = useState([]);
  const [searchCol, setSearchCol] = useState(initialCol);
  const [searchText, setSearchText] = useState(initialSearch);

  // --- FETCH FUNCTION ---
  const fetchData = (isSearch = false) => {
    setLoading(true);
    setError(null);

    let url = `${API_BASE}${endpoint}`;
    
    // Prefer state values, fall back to props if just mounting
    const col = isSearch ? searchCol : (initialCol || searchCol);
    const term = isSearch ? searchText : (initialSearch || searchText);

    if (col && term) {
      url += `?column=${encodeURIComponent(col)}&search=${encodeURIComponent(term)}`;
    }

    authFetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`Server Error: ${res.status}`);
        return res.json();
      })
      .then(response => {
        setData(response.rows);
        setTotalCount(response.total);

        // Populate headers if needed
        if (response.rows.length > 0 && headers.length === 0) {
          const keys = Object.keys(response.rows[0]);
          setHeaders(keys);
          // Only set default col if user didn't provide one in URL
          if (!initialCol) setSearchCol(keys[0]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError(err.message);
        setLoading(false);
      });
  };

  // Initial Load
  useEffect(() => {
    // If URL params exist, trigger search immediately
    if (initialCol && initialSearch) {
        setSearchCol(initialCol);
        setSearchText(initialSearch);
        fetchData(true);
    } else {
        setSearchText(''); 
        fetchData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const handleSearch = (e) => { e.preventDefault(); fetchData(true); };
  const handleClear = () => { setSearchText(''); fetchData(false); };

  if (loading && headers.length === 0) return <div style={{ padding: '20px' }}>Loading Data...</div>;
  if (error) return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;

  return (
    <div>
      <Link to="/real-estate/raw" style={{ display: 'block', marginBottom: '20px', color: '#64748b', textDecoration: 'none', fontSize: '14px' }}>← Back to Tables</Link>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '20px' }}>
        <h1 style={{ color: '#1e293b', margin: 0 }}>{title || "Transactions"}</h1>
        <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>Found <b>{totalCount.toLocaleString()}</b> records.</p>
      </div>

      <form onSubmit={handleSearch} style={searchBarStyle}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 150px' }}>
            <label style={labelStyle}>Column:</label>
            <select value={searchCol} onChange={(e) => setSearchCol(e.target.value)} style={{...inputStyle, width: '100%'}}>
              {headers.map(h => (<option key={h} value={h}>{h.replace(/_/g, ' ')}</option>))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flex: '10 1 200px' }}>
            <label style={labelStyle}>Query:</label>
            <input type="text" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: 'auto', paddingTop: '4px' }}>
            <button type="submit" style={buttonStyle}><Search size={16} /> Search</button>
            {searchText && <button type="button" onClick={handleClear} style={clearButtonStyle}><RotateCcw size={16} /> Reset</button>}
          </div>
        </div>
      </form>

      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <tr>{headers.map((key) => (<th key={key} style={thStyle}>{key.replace(/_/g, ' ')}</th>))}</tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} style={{ borderBottom: '1px solid #f1f5f9' }}>
                {headers.map((key) => {
                    let val = row[key];
                    if (val && (key.includes('CENA') || key.includes('ODSKODNINA'))) {
                       const num = parseFloat(val.toString().replace(',', '.'));
                       if (!isNaN(num)) val = num.toLocaleString('sl-SI', { style: 'currency', currency: 'EUR' });
                    }
                    return <td key={key} style={tdStyle}>{val || '-'}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Styles
const thStyle = { padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', whiteSpace: 'nowrap', textTransform: 'uppercase' };
const tdStyle = { padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' };
const searchBarStyle = { backgroundColor: '#fff', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' };
const inputStyle = { padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' };
const buttonStyle = { marginTop: '18px', padding: '9px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' };
const clearButtonStyle = { marginTop: '18px', padding: '9px 16px', backgroundColor: '#fff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };

export default TransactionTable;